import { useNavigate } from "react-router-dom";

import { OptionsDropdown } from "src/base-components/OptionsDropdown/OptionsDropdown";
import { CaseTemplateModal } from "src/cases/templates/CaseTemplateModal";
import { CreateFromExistingTemplateModal } from "src/cases/templates/CreateFromExistingTemplateModal";
import { useAddTemplateDisabledReason } from "src/cases/templates/useAddTemplateDisabledReason";
import { useModal } from "src/design-system/Modal";
import { Tooltip } from "src/design-system/Tooltip";
import {
  DashboardPageParams,
  getUrlToCaseTemplateVersionsPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const useAddTemplate = () => {
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const navigate = useNavigate();

  const {
    isOpen: isCreateOpen,
    openModal: openCreate,
    closeModal: closeCreate,
    afterLeave: createAfterLeave,
  } = useModal();

  const fromTemplateModal = useModal();

  const modals = (
    <>
      <CaseTemplateModal
        afterLeave={createAfterLeave}
        open={isCreateOpen}
        onClose={closeCreate}
        onCreateSuccess={(template) => {
          navigate(getUrlToCaseTemplateVersionsPage(orgId, wsId, template.id));
        }}
      />
      <CreateFromExistingTemplateModal
        open={fromTemplateModal.isOpen}
        onClose={fromTemplateModal.closeModal}
      />
    </>
  );

  return {
    openFromScratch: () => openCreate(),
    openFromTemplate: () => fromTemplateModal.openModal(),
    modals,
  };
};

type AddTemplateDropdownProps = {
  children: React.ReactNode;
  buttonDataLoc?: string;
};

export const AddTemplateDropdown: React.FC<AddTemplateDropdownProps> = ({
  children,
  buttonDataLoc,
}) => {
  const { openFromScratch, openFromTemplate, modals } = useAddTemplate();
  const disabledReason = useAddTemplateDisabledReason();

  return (
    <>
      <OptionsDropdown
        buttonDataLoc={buttonDataLoc}
        disabled={Boolean(disabledReason)}
        elements={[
          { key: "From scratch", action: openFromScratch },
          { key: "From a template", action: openFromTemplate },
        ]}
        placement="bottom start"
      >
        <Tooltip
          body={disabledReason}
          disabled={!disabledReason}
          placement="right"
          asChild
        >
          {children}
        </Tooltip>
      </OptionsDropdown>
      {modals}
    </>
  );
};
